/**
 * useSwipeNavigation.js
 *
 * Lightweight touch-swipe hook for switching between tabs on mobile.
 *
 * Usage:
 *   const swipeHandlers = useSwipeNavigation({
 *     tabs: ['today', 'build', 'company', 'team'],
 *     activeTab,
 *     setActiveTab,
 *     enabled: isMobile,
 *   });
 *   <main {...swipeHandlers}> ... </main>
 *
 * Behaviour:
 *   - Swipe LEFT  → next tab
 *   - Swipe RIGHT → previous tab
 *   - Ignores vertical scrolls (vertical delta > horizontal delta)
 *   - Ignores swipes that start on interactive elements (buttons, inputs,
 *     scrollable horizontal containers) so it doesn't fight existing UI
 */

import { useRef, useCallback } from 'react';

const MIN_SWIPE_DISTANCE = 60;          // px — must travel at least this far
const MAX_VERTICAL_DRIFT_RATIO = 0.6;   // |dy| < 0.6 * |dx| to count as horizontal
const EDGE_IGNORE_PX = 20;              // ignore swipes that start at screen edges (iOS back-swipe)

const isInteractiveTarget = (el) => {
  if (!el) return false;
  // Walk up a few ancestors checking for elements that own their own gestures
  let node = el;
  for (let i = 0; i < 6 && node; i++) {
    if (node.nodeType !== 1) { node = node.parentNode; continue; }
    const tag = node.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON') return true;
    if (node.getAttribute && node.getAttribute('data-no-swipe') === 'true') return true;
    // Horizontally scrollable containers should keep their own panning
    const style = node instanceof Element ? window.getComputedStyle(node) : null;
    if (style) {
      const ox = style.overflowX;
      if ((ox === 'auto' || ox === 'scroll') && node.scrollWidth > node.clientWidth + 4) return true;
    }
    node = node.parentNode;
  }
  return false;
};

export default function useSwipeNavigation({ tabs, activeTab, setActiveTab, enabled = true }) {
  const startRef = useRef(null);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    if (!e.touches || e.touches.length !== 1) { startRef.current = null; return; }
    const t = e.touches[0];
    // Ignore swipes that start near the screen edge (avoid clashing with iOS back gesture)
    if (t.clientX < EDGE_IGNORE_PX || t.clientX > window.innerWidth - EDGE_IGNORE_PX) {
      startRef.current = null;
      return;
    }
    if (isInteractiveTarget(e.target)) { startRef.current = null; return; }
    startRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  }, [enabled]);

  const onTouchMove = useCallback((e) => {
    if (!startRef.current || !e.touches || e.touches.length !== 1) return;
    const t = e.touches[0];
    const dx = t.clientX - startRef.current.x;
    const dy = t.clientY - startRef.current.y;
    // If the gesture turns vertical, abandon it so the page can scroll normally
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 12) {
      startRef.current = null;
    }
  }, []);

  const onTouchEnd = useCallback((e) => {
    const start = startRef.current;
    startRef.current = null;
    if (!start || !enabled) return;
    const touch = (e.changedTouches && e.changedTouches[0]) || null;
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < MIN_SWIPE_DISTANCE) return;
    if (Math.abs(dy) > Math.abs(dx) * MAX_VERTICAL_DRIFT_RATIO) return;
    // Quick flicks too — limit to ~1.2s
    if (Date.now() - start.time > 1200) return;

    const idx = tabs.indexOf(activeTab);
    if (idx === -1) return;

    if (dx < 0 && idx < tabs.length - 1) {
      // Swipe LEFT → next tab
      setActiveTab(tabs[idx + 1]);
    } else if (dx > 0 && idx > 0) {
      // Swipe RIGHT → previous tab
      setActiveTab(tabs[idx - 1]);
    }
  }, [enabled, tabs, activeTab, setActiveTab]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
